import { useState } from "react";
import { Clock, MapPin, Users } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import ParticipantList from "./ParticipantList";

interface PendingMatchCardProps {
  title?: string;
  date?: string;
  time?: string;
  court?: string;
  players?: string[];
  modalidad?: 'singles' | 'doubles';
  className?: string;
  showParticipants?: boolean;
  eventType?: 'tournament' | 'league';
  eventTitle?: string;
}

const PendingMatchCard = ({ 
  title = "Partido Programado",
  date,
  time,
  court,
  players = [],
  modalidad = 'doubles',
  className = "",
  showParticipants = true,
  eventType = 'tournament',
  eventTitle = ''
}: PendingMatchCardProps) => {
  const [showParticipantList, setShowParticipantList] = useState(false);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', { 
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  if (showParticipantList) {
    return (
      <div className="space-y-4">
        <Button 
          variant="outline" 
          onClick={() => setShowParticipantList(false)}
          className="mb-4"
        >
          ← Volver
        </Button>
        <ParticipantList 
          gameType={modalidad}
          eventType={eventType}
          eventTitle={eventTitle}
        />
      </div>
    );
  }
  return (
    <Card className={`bg-gradient-to-br from-slate-50 to-primary/5 border-slate-200 ${className}`}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-slate-700">
            <Clock className="h-5 w-5 text-slate-400" />
            <span className="text-lg">{title}</span>
          </CardTitle>
          {showParticipants && (
            <Button 
              variant="outline"
              size="sm"
              onClick={() => setShowParticipantList(true)}
              className="text-slate-600 border-slate-300 hover:bg-slate-50"
            >
              <Users className="h-4 w-4 mr-2" />
              Ver participantes
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Match Info */}
        {(date || time || court) && (
          <div className="flex items-center justify-center space-x-6 text-sm">
            {date && (
              <div className="flex items-center">
                <Clock className="h-4 w-4 text-slate-500 mr-2" />
                <span className="font-medium text-slate-700">{formatDate(date)}</span>
                {time && <span className="text-slate-600 ml-2">{time}</span>}
              </div>
            )}
            {court && (
              <div className="flex items-center">
                <MapPin className="h-4 w-4 text-slate-500 mr-2" />
                <span className="font-medium text-slate-700">Cancha {court}</span>
              </div>
            )}
          </div>
        )}

        {/* Players Display */}
        <div className="text-center">
          <div className="flex flex-col items-center space-y-3">
            {players.length > 0 ? (
              <>
                <div className="font-semibold text-lg text-slate-700 bg-white/50 backdrop-blur-sm px-4 py-2 rounded-lg">
                  {players[0]}
                </div>
                <div className="text-slate-400 font-medium">VS</div>
                <div className="font-semibold text-lg text-slate-700 bg-white/50 backdrop-blur-sm px-4 py-2 rounded-lg">
                  {players[1] || "Por confirmar"}
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center space-y-3">
                <div className="font-semibold text-lg text-slate-500 bg-white/50 backdrop-blur-sm px-4 py-2 rounded-lg border-2 border-dashed border-slate-300">
                  Participante 1
                </div>
                <div className="text-slate-400 font-medium">VS</div>
                <div className="font-semibold text-lg text-slate-500 bg-white/50 backdrop-blur-sm px-4 py-2 rounded-lg border-2 border-dashed border-slate-300">
                  Participante 2
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Status Badge */}
        <div className="flex justify-center">
          <Badge 
            variant="outline" 
            className="border-primary/30 text-primary bg-primary/5 px-3 py-1"
          >
            Por confirmar
          </Badge>
        </div>

        {/* Info Message */}
        <div className="text-center">
          <p className="text-xs text-slate-500 leading-relaxed">
            Los emparejamientos se confirmarán cuando inicie el evento
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default PendingMatchCard;